import TextField from "@mui/material/TextField"
import MuiAutocomplete, {
  AutocompleteProps as MuiAutocompleteProps,
} from "@mui/material/Autocomplete"
import { useField } from "react-final-form"

interface AutocompleteProps<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined
> extends Omit<
    MuiAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
    "value" | "isOptionEqualToValue" | "onChange" | "renderInput"
  > {
  name: string
  label: string
  afterChange?: (value: T) => void
}

export default function Autocomplete<
  T extends { id: number },
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined
>(props: AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>) {
  const { name, label, afterChange, options, multiple, ...rest } = props

  const {
    input: { value, onChange },
    meta: { touched, error, submitError },
  } = useField(name, {
    parse: (value, _name) => {
      // normalize empty value
      if (value === undefined) {
        return null
      }

      return value
    },
  })

  let muiValue: any
  if (multiple) {
    muiValue = options.filter((option) => value.includes(option.id))
  } else {
    muiValue = options.find((option) => option.id === value) || null
  }

  const normalizedError = Array.isArray(error) ? error.join(", ") : error || submitError

  return (
    <MuiAutocomplete
      options={options}
      value={muiValue}
      isOptionEqualToValue={(option: any, value: any) => option === value}
      multiple={multiple}
      onChange={(e, option: any) => {
        if (multiple) {
          onChange(option.map((o: any) => o.id))
        } else {
          onChange(option?.id)
        }

        afterChange?.(option)
      }}
      renderInput={(params) => {
        return (
          <TextField
            label={label}
            name={name}
            {...params}
            InputLabelProps={{ shrink: true }}
            InputProps={{ ...params.InputProps }}
            fullWidth={true}
            error={touched && normalizedError ? true : false}
            helperText={touched && normalizedError && normalizedError}
          />
        )
      }}
      {...rest}
    />
  )
}
